import { useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, ShieldCheck, CreditCard, Zap, Globe } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import ContactForm from "@/components/ContactForm";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };
const heroVisualInitial = { opacity: 0, scale: 0.95 };
const heroVisualAnimate = { opacity: 1, scale: 1 };
const heroVisualTransition = { duration: 0.5, delay: 0.2 };

const PLATFORMS = {
  woocommerce: {
    name: "WooCommerce",
    headline: "Accept payments on your WooCommerce store",
    desc: "Official WooCommerce plugin with support for all major cards, Apple Pay, Google Pay, and 3D Secure. Clear pricing, next-day settlement, and real support.",
    why: "WooCommerce is the world's most popular e-commerce platform — and CanDo Payments offers a purpose-built plugin that drops straight into your checkout. No complex setup, no hidden fees, and if anything goes wrong, you speak to a real person in the UK.",
    features: [
      "Official WooCommerce plugin — fully supported and regularly updated",
      "Visa, Mastercard, Amex, Apple Pay and Google Pay built in",
      "3D Secure and AI-powered fraud protection as standard",
      "Recurring billing for subscriptions via tokenisation",
      "Next-day settlement into your UK bank account",
      "Clear, transparent pricing — no per-transaction surprises",
    ],
  },
  shopify: {
    name: "Shopify",
    headline: "Better rates and real support for your Shopify store",
    desc: "A lower-cost alternative to Shopify Payments with dedicated UK support. Keep more of what you earn.",
    why: "Shopify Payments is convenient but locks you into one provider with little transparency. CanDo Payments gives Shopify merchants competitive rates, next-day settlement, and a named account manager who picks up the phone.",
    features: [
      "Seamless Shopify integration — no extra plugins to manage",
      "Lower transaction fees than Shopify Payments for most UK merchants",
      "Next-day settlement into your UK bank account",
      "Multi-currency support for international sales",
      "A real account manager — not automated support tickets",
      "No lock-in contract — switch back anytime",
    ],
  },
  magento: {
    name: "Magento 2",
    headline: "Enterprise-grade payments for your Magento 2 store",
    desc: "Fully certified Magento 2 module with Elavon's infrastructure behind it. For merchants running serious e-commerce.",
    why: "Magento 2 is the platform for businesses that need full control. Our certified module brings enterprise-grade payment processing without enterprise-grade complexity or support wait times.",
    features: [
      "Fully certified Magento 2 module with ongoing maintenance",
      "Hosted payment page or fully customised API integration",
      "Multi-store, multi-currency, multi-language support",
      "Advanced fraud protection and 3D Secure compliance",
      "Dedicated solution engineer for setup support",
      "Backed by Elavon — same infrastructure as global brands",
    ],
  },
  prestashop: {
    name: "PrestaShop",
    headline: "Simple, reliable payments for your PrestaShop store",
    desc: "Native PrestaShop module with all major cards, digital wallets, and clear pricing.",
    why: "PrestaShop merchants deserve a payment provider that matches the platform's simplicity. Our native module installs in minutes and gives you everything you need — all major cards, Apple Pay, Google Pay, and PCI-compliant hosted pages.",
    features: [
      "Native PrestaShop module — maintained by our team",
      "All major card schemes plus Apple Pay and Google Pay",
      "Multi-currency support for cross-border selling",
      "PCI DSS compliant hosted payment pages",
      "Clear, flat pricing with no hidden add-ons",
      "UK-based support that responds in minutes, not days",
    ],
  },
  bigcommerce: {
    name: "BigCommerce",
    headline: "Keep more of what you earn on BigCommerce",
    desc: "Lower fees, better support, and the same enterprise-grade reliability as default providers.",
    why: "BigCommerce merchants are scaling fast and need a payment partner that keeps up. CanDo Payments integrates cleanly with BigCommerce's checkout and delivers serious infrastructure at a lower cost.",
    features: [
      "Clean BigCommerce integration — no third-party workarounds",
      "Lower fees than most default BigCommerce payment options",
      "Built for scale — Elavon processes for global brands",
      "Tokenisation and recurring billing for subscriptions",
      "Real account management, not a chatbot",
      "Next-day settlement as standard",
    ],
  },
  opencart: {
    name: "OpenCart",
    headline: "Fast, affordable payments for your OpenCart store",
    desc: "Lightweight, plug-and-play payment module for OpenCart. Most stores live within a working day.",
    why: "OpenCart is fast, flexible, and cost-effective. So is CanDo Payments. Our module gets you taking payments in minutes with competitive rates and no overheads.",
    features: [
      "Fully supported OpenCart module",
      "Fast setup — most stores live within a working day",
      "All major card schemes plus digital wallets",
      "PCI-compliant hosted payment pages",
      "Transparent pricing your customers will understand",
      "Direct support from our UK team",
    ],
  },
  drupal: {
    name: "Drupal",
    headline: "Flexible payment integration for Drupal Commerce",
    desc: "Hosted pages, full API, or hybrid — your stack, your rules. With real support behind it.",
    why: "Drupal Commerce needs a payment layer that matches its flexibility. Our API gives you complete control over the checkout flow, with hosted pages available as a quick-start option.",
    features: [
      "Drupal Commerce integration via flexible REST API",
      "Hosted or fully customised checkout flows",
      "All major card schemes and alternative payment methods",
      "Engineer-to-engineer support when you need it",
      "PCI DSS compliance built in",
      "Transparent, honest pricing",
    ],
  },
};

const OTHER_PLATFORMS = Object.entries(PLATFORMS)
  .map(([slug, data]) => ({ slug, name: data.name }));

export default function MerchantPlatformPage() {
  const { platform } = useParams();
  const data = PLATFORMS[platform];

  if (!data) return <Navigate to="/integrations" replace />;

  const others = OTHER_PLATFORMS.filter((p) => p.slug !== platform).slice(0, 4);

  return (
    <div>
      <SEOHead title={`${data.name} Payments`} description={data.desc} path={`/integrations/${platform}`} />

      {/* Hero */}
      <section className="hero-gradient py-16 md:py-24" data-testid={`merchant-${platform}-hero`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">{`${data.name} Payments`}</motion.p>
              <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">{data.headline}</motion.h1>
              <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-8">{data.desc}</motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <Link to={`/contact?product=${encodeURIComponent(data.name + ' Payments')}&category=integrations&source=${platform}-merchant-page`} className="cta-primary text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-all btn-shine" data-testid={`merchant-${platform}-cta`}>
                  {`Get ${data.name} pricing`}
                  <ArrowRight size={16} />
                </Link>
                <Link to="/quote/online" className="bg-brand-pink-light text-brand-pink hover:bg-brand-pink/15 border border-brand-pink/30 font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors">
                  Estimate my costs
                </Link>
              </motion.div>
            </motion.div>
            <motion.div initial={heroVisualInitial} animate={heroVisualAnimate} transition={heroVisualTransition} className="hidden lg:flex items-center justify-center">
              <div className="bg-white rounded-2xl shadow-lg border border-brand-light-grey/60 p-6 max-w-xs">
                <div className="flex items-center gap-2 mb-3">
                  <Globe size={18} className="text-brand-aqua" />
                  <span className="font-heading font-bold text-brand-black">{data.name}</span>
                  <span className="ml-auto text-[10px] font-bold text-brand-aqua-dark bg-brand-aqua-light px-2 py-0.5 rounded-full">Live</span>
                </div>
                <div className="space-y-2">
                  {data.features.slice(0, 4).map((f) => (
                    <div key={f} className="flex items-start gap-2 text-xs text-brand-grey">
                      <CheckCircle size={12} className="text-brand-aqua mt-0.5 flex-shrink-0" />
                      <span>{f.split("—")[0].trim()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-4">{`Why choose CanDo for ${data.name}?`}</h2>
              <p className="text-brand-grey leading-relaxed mb-6">{data.why}</p>
              <div className="space-y-3">
                {data.features.map((f) => (
                  <div key={f} className="flex items-start gap-2 text-sm text-brand-black">
                    <CheckCircle size={16} className="text-brand-aqua flex-shrink-0 mt-0.5" />{f}
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
              <h3 className="font-heading text-xl font-bold text-brand-black mb-2">{`Get set up on ${data.name}`}</h3>
              <p className="text-sm text-brand-grey mb-4">Tell us about your store and we&apos;ll get your payments live — usually within 24 hours.</p>
              <ContactForm context={{ platformLabel: data.name, platform: platform }} />
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, title: "PCI DSS Compliant", desc: "Tokenisation, 3D Secure, and fraud scoring built in." },
            { icon: CreditCard, title: "All Major Cards", desc: "Visa, Mastercard, Amex, Apple Pay, Google Pay." },
            { icon: Zap, title: "Next-Day Settlement", desc: "Into your UK bank account, as standard." },
          ].map((item) => (
            <div key={item.title} className="flex items-start gap-3 p-4 bg-white border border-brand-light-grey rounded-xl">
              <item.icon size={20} className="text-brand-aqua flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-brand-black">{item.title}</p>
                <p className="text-xs text-brand-grey">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Other platforms */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl font-bold text-brand-black tracking-tight mb-6 text-center">Also available for</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl mx-auto">
            {others.map((p) => (
              <Link key={p.slug} to={`/integrations/${p.slug}`} className="text-center py-3 px-4 bg-gray-50 border border-brand-light-grey rounded-xl text-sm font-semibold text-brand-black hover:bg-brand-aqua-light hover:border-brand-aqua/30 hover:text-brand-aqua-dark transition-all">
                {p.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
